/**
 * Helper functions based on https://github.com/justmoon/node-extend under the MIT License
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014 Stefan Thomas
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
 * LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
 * WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import {ExtendOptions} from './extend-options.interface';

const hasOwn = Object.prototype.hasOwnProperty,
  toStr = Object.prototype.toString;

function isArray(arr) {
  if (typeof Array.isArray === 'function') {
    return Array.isArray(arr);
  }

  return toStr.call(arr) === '[object Array]';
}

function isPlainObject(obj) {
  if (!obj || toStr.call(obj) !== '[object Object]') {
    return false;
  }

  const hasOwnConstructor = hasOwn.call(obj, 'constructor'),
    hasIsPrototypeOf = obj.constructor && obj.constructor.prototype && hasOwn.call(obj.constructor.prototype, 'isPrototypeOf');
  // Not own constructor property must be Object
  if (obj.constructor && !hasOwnConstructor && !hasIsPrototypeOf) {
    return false;
  }

  // Own properties are enumerated firstly, so to speed up,
  // if last one is own, then all properties are own.
  let key;
  for (key in obj) { /**/
  }

  return typeof key === 'undefined' || hasOwn.call(obj, key);
}

/**
 * Extend objects with no deep cloning
 *
 * @param {any[]} args an array of objects you want to concatenate
 * @returns {object}
 */
export function extend(...args: any[]): {} {
  let i = 0;

  const out = {};

  for (i; i < args.length; i++) {
    if (!args[i]) {
      continue;
    }
    for (const key in args) {
      if (args[i].hasOwnProperty(key)) {
        out[key] = args[i][key];
      }
    }
  }

  return out;
}

/**
 * Extend objects with deep cloning
 * @param {any} out an array of objects you want to concatenate
 * @param {any[]} args
 * @param {ExtendOptions} options
 * @returns {object}
 */
export function deepExtend(out, args: any[], options: ExtendOptions = {
  mergeArray: true,
  dedupeArray: true,
}): {} {
  out = out || {};
  let i = 0,
    obj;

  for (i; i < args.length; i++) {
    obj = Object.assign({}, args[i]);

    if (!obj) {
      continue;
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const src = out[key],
          copy = obj[key];
        let clone;
        if (src !== copy) {
          if (isArray(copy)) {
            clone = src && isArray(src) ? src : [];
          }
          else {
            clone = src && isPlainObject(src) ? src : {};
          }
          if (typeof copy === 'object') {
            out[key] = deepExtend(clone, [copy], options);
          }
          else if (isArray(out) && options.mergeArray) {
            out.push(copy);
          }
          else {
            out[key] = copy;
          }
        }
      }
    }
  }
  if (isArray(out) && options.dedupeArray) {
    out = out.filter((elm, pos, index) => out.indexOf(elm) === pos);
  }

  return out;
}



// WEBPACK FOOTER //
// ./src/utils/object/extend.utils.ts